import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  CircularProgress,
  Badge,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Hotel as HotelIcon, Event as EventIcon } from '@mui/icons-material';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { collection, query, where, getDocs, getFirestore, Timestamp } from 'firebase/firestore';

// Add the plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface Booking {
  id: string;
  checkIn: Timestamp;
  checkOut: Timestamp;
  rooms: Array<{
    name: string;
    quantity: number;
  }>;
  guestInfo: {
    firstName: string;
    lastName: string;
  };
}

interface RoomAvailabilityMatrixProps {
  selectedRestaurant: any;
}

const RoomAvailabilityMatrix: React.FC<RoomAvailabilityMatrixProps> = ({ selectedRestaurant }) => {
  const theme = useTheme();
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(dayjs().add(7, 'day'));
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchBookings = async () => {
    if (!selectedRestaurant?.docId) return;

    try {
      setLoading(true);
      const db = getFirestore();
      const bookingsRef = collection(db, 'room-bookings');
      
      const _startTimestamp = Timestamp.fromDate(
        startDate?.startOf('day').toDate() || new Date()
      );
      
      const endTimestamp = Timestamp.fromDate(
        endDate?.endOf('day').toDate() || new Date()
      );
      
      // Query for bookings that:
      // 1. Start within the selected range OR
      // 2. End within the selected range OR
      // 3. Span across the entire selected range
      const q = query(
        bookingsRef,
        where('restaurantId', '==', selectedRestaurant.docId),
        where('checkIn', '<=', endTimestamp) // Booking starts before or on the end date
      );

      const querySnapshot = await getDocs(q);
      const bookingsData = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Booking))
        .filter(booking => {
          const bookingCheckIn = dayjs(booking.checkIn.toDate());
          const bookingCheckOut = dayjs(booking.checkOut.toDate());
          const selectedStart = startDate?.startOf('day');
          const selectedEnd = endDate?.endOf('day');

          // A booking overlaps if:
          // 1. Booking check-in is within the selected range OR
          // 2. Booking check-out is within the selected range OR
          // 3. Booking spans the entire selected range
          return (
            (bookingCheckIn.isSameOrAfter(selectedStart) && bookingCheckIn.isBefore(selectedEnd)) ||
            (bookingCheckOut.isAfter(selectedStart) && bookingCheckOut.isSameOrBefore(selectedEnd)) ||
            (bookingCheckIn.isBefore(selectedStart) && bookingCheckOut.isAfter(selectedEnd))
          );
        });

      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchBookings uses startDate/endDate
  useEffect(() => {
    fetchBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchBookings uses startDate/endDate
  }, [selectedRestaurant?.docId, startDate, endDate]);

  const getDatesInRange = () => {
    const dates = [];
    let currentDate = startDate?.startOf('day');
    const end = endDate?.endOf('day');

    while (currentDate && end && currentDate.isBefore(end)) {
      dates.push(currentDate);
      currentDate = currentDate.add(1, 'day');
    }
    return dates;
  };

  const getRoomAvailability = (roomName: string, date: dayjs.Dayjs) => {
    const booking = bookings.find(b => {
      const checkIn = dayjs(b.checkIn.toDate()).startOf('day');
      const checkOut = dayjs(b.checkOut.toDate()).startOf('day');
      const currentDate = date.startOf('day');
      
      // Room is booked if current date is between check-in and check-out (exclusive)
      // Check-out date is not counted as booked since it's available from 11 AM
      return b.rooms.some((r: any) => r.name === roomName) &&
             currentDate.isSameOrAfter(checkIn) &&
             currentDate.isBefore(checkOut);
    });

    if (!booking) return { status: 'available', booking: null };
    return { status: 'booked', booking };
  };

  const getAvailableRoomCount = (roomName: string, date: dayjs.Dayjs) => {
    const room = selectedRestaurant?.roomDetails?.find((r: any) => r.name === roomName);
    if (!room) return 0;

    const bookedRooms = bookings.filter(b => {
      const checkIn = dayjs(b.checkIn.toDate()).startOf('day');
      const checkOut = dayjs(b.checkOut.toDate()).startOf('day');
      const currentDate = date.startOf('day');
      
      // Room is booked if current date is between check-in and check-out (exclusive)
      // Check-out date is not counted as booked since it's available from 11 AM
      return b.rooms.some((r: any) => r.name === roomName) &&
             currentDate.isSameOrAfter(checkIn) &&
             currentDate.isBefore(checkOut);
    }).reduce((acc, booking) => {
      const roomBooking = booking.rooms.find((r: any) => r.name === roomName);
      return acc + (roomBooking?.quantity || 0);
    }, 0);

    return Math.max(0, room.totalRooms - bookedRooms);
  };

  const getStatusColor = (status: string, availableCount: number, totalRooms: number) => {
    if (availableCount === 0) return 'error';
    const availabilityPercentage = (availableCount / totalRooms) * 100;
    if (availabilityPercentage < 40) return 'warning';
    if (availabilityPercentage < 70) return 'info';
    return 'success';
  };

  const getStatusBackgroundColor = (status: string, availableCount: number, totalRooms: number) => {
    if (availableCount === 0) return alpha(theme.palette.error.main, 0.1);
    const availabilityPercentage = (availableCount / totalRooms) * 100;
    if (availabilityPercentage < 40) return alpha(theme.palette.warning.main, 0.1);
    if (availabilityPercentage < 70) return alpha(theme.palette.info.main, 0.1);
    return alpha(theme.palette.success.main, 0.1);
  };

  const getStatusTextColor = (status: string, availableCount: number, totalRooms: number) => {
    if (availableCount === 0) return theme.palette.error.main;
    const availabilityPercentage = (availableCount / totalRooms) * 100;
    if (availabilityPercentage < 40) return theme.palette.warning.main;
    if (availabilityPercentage < 70) return theme.palette.info.main;
    return theme.palette.success.main;
  };

  const _getTotalAvailableRooms = (date: dayjs.Dayjs) => {
    return selectedRestaurant?.roomDetails?.reduce((acc: number, room: any) => {
      return acc + getAvailableRoomCount(room.name, date);
    }, 0) || 0;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: theme.palette.primary.main,
        fontWeight: 600,
        mb: 3
      }}>
        <HotelIcon /> Room Availability Matrix
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ 
            background: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <EventIcon color="primary" />
                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 500 }}>
                  Select Date Range
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    sx={{ 
                      width: '200px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    minDate={startDate}
                    sx={{ 
                      width: '200px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Room Type
                    </TableCell>
                    {getDatesInRange().map((date) => (
                      <TableCell 
                        key={date.format('YYYY-MM-DD')} 
                        align="center"
                        sx={{ 
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">
                            {date.format('MMM DD')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {date.format('ddd')}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={getDatesInRange().length + 1} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedRestaurant?.roomDetails?.map((room: any) => (
                      <TableRow 
                        key={room.name}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row"
                          sx={{ 
                            borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            fontWeight: 500,
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {room.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total: {room.totalRooms} rooms
                            </Typography>
                          </Box>
                        </TableCell>
                        {getDatesInRange().map((date) => {
                          const availability = getRoomAvailability(room.name, date);
                          const availableCount = getAvailableRoomCount(room.name, date);
                          return (
                            <TableCell 
                              key={date.format('YYYY-MM-DD')} 
                              align="center"
                              sx={{ 
                                borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              }}
                            >
                              <Tooltip 
                                title={
                                  availability.status === 'booked' 
                                    ? `Booked by ${availability.booking?.guestInfo?.firstName} ${availability.booking?.guestInfo?.lastName}`
                                    : `${availableCount} rooms available`
                                }
                              >
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  alignItems: 'center', 
                                  gap: 0.5,
                                  p: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: getStatusBackgroundColor(
                                    availability.status,
                                    availableCount,
                                    room.totalRooms
                                  ),
                                }}>
                                  <Chip
                                    label={availability.status === 'booked' ? 'B' : 'A'}
                                    color={getStatusColor(
                                      availability.status,
                                      availableCount,
                                      room.totalRooms
                                    )}
                                    size="small"
                                    sx={{ 
                                      fontWeight: 600,
                                      '& .MuiChip-label': {
                                        px: 1,
                                      },
                                    }}
                                  />
                                  <Badge 
                                    badgeContent={availableCount} 
                                    color="primary"
                                    sx={{
                                      '& .MuiBadge-badge': {
                                        backgroundColor: 'white',
                                        color: getStatusTextColor(
                                          availability.status,
                                          availableCount,
                                          room.totalRooms
                                        ),
                                        fontSize: '0.75rem',
                                        padding: '0 4px',
                                        minWidth: '20px',
                                        height: '20px',
                                        borderRadius: '10px',
                                        fontWeight: 600,
                                        border: `1px solid ${getStatusTextColor(
                                          availability.status,
                                          availableCount,
                                          room.totalRooms
                                        )}`,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                      }
                                    }}
                                  >
                                    <Box sx={{ width: 8, height: 8 }} />
                                  </Badge>
                                </Box>
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const mapStateToProps = (state: any) => {
  return {
    selectedRestaurant: state.common?.selectedRestaurant
  };
};

export default connect(mapStateToProps)(RoomAvailabilityMatrix); 